import {
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import AddToArtlistIcon from '@/assets/all-icons/add-to-artlist-icon';
import LimitedEditionIcon from '@/assets/all-icons/limited-edition-icon.jsx';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import usePreferredLocale from '@/hooks/usePreferredLocale';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ArtworkHeaderMobile from '@/pages/artwork/artwork-header-mobile';
import AddToArtlist from '@/pages/artwork/artwork-header-mobile/options/add-to-artlist';
import SoldPriceValue from '@/pages/artwork/components/SoldPriceValue';
import AuctionTimeLeftIndicator from '@/pages/artwork/content-section/components/AuctionTimeLeftIndicator';
import PicturesSection from '@/pages/artwork/pictures-section';
import { getLocalizedArtworkMedium } from '@/pages/artwork/utils/artworkMediumDisplay.utils';
import { formatArtworkTitleForDisplay } from '@/pages/artwork/utils/artworkTitleDisplay.utils';
import {
  getArtworkAiView,
  getArtworkSaleSummary,
  getArtworkShippingConfig,
  isArtworkOriginalSold,
  isLimitedEditionHoldingArtwork,
} from '@/pages/artwork/utils/artworkView.selectors';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
// import BuyLimitedEditionButton from '@/shared-components/artwork/buy-limited-edition';
// import BuyOriginalButton from '@/shared-components/artwork/buy-original';
import ArtworkSocials from '@/shared-components/artwork/components/ArtworkSocials';
import ArtworkTitle from '@/shared-components/artwork/components/ArtworkTitle';
import AutoMarquee from '@/shared-components/text/components/AutoMarquee';
// Bottom sheet moved to top-level Artwork component.
import Text from '@/shared-components/text/components/Text';
import { shouldHideCollectorInteractions } from '@/utils/artworkVisibility';
import NumberUtils from '@/utils/number.utils';

// Mobile details-only content will live in the bottom-sheet outside this view.
// Keep content here minimal to ensure one-page layout per artwork.
// import AudioPlayer from '../content-section/components/AudioPlayer';
// import Description from '../content-section/components/Description';
import Socials from '../content-section/socials';

// import ArtworkMoreDetails from './components/artwork-more-details/ArtworkMoreDetails';
// Header moved outside Swiper to avoid being affected by transform stacking.
// import ArtworkViewHeader from './components/artwork-view-header/ArtworkViewHeader';
import './index.css';

function formatPriceWithCurrency(value, currency) {
  if (value === null || value === undefined) {
    return null;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return NumberUtils.getArtworkPrice(numericValue, currency);
  }

  if (typeof value === 'string' && value.trim()) {
    return `${value.trim()} ${currency}`;
  }

  return null;
}

export default function ArtworkMobileView({ selectedSocial, setSelectedSocial, isStandaloneVisit = false }) {
  const { artworkData, socialsData, setSocialsData, onUnlockSoldPrice, soldPricePending } = useContext(ArtworkDataContext);
  const [openAddToArtlist, setOpenAddToArtlist] = useState(false);
  const { state } = useStateHandler();
  const location = useLocation();
  const locale = usePreferredLocale();
  const tPurchaseFooter = useTranslations('common.shared.artwork_view.purchase_footer');
  const privateActionHandler = useProtectAction({ typeOfProtection: 'USER_ACCOUNT_REQUIRED' });

  const {
    artwork_title: artworkTitle,
    artwork_medium: mediumValue,
    artwork_size: artworkSize,
    artwork_year: yearValue,
    artwork_price: artworkPrice,
    artwork_currency: artworkCurrency,
  } = artworkData.artwork_about;
  const titleText = formatArtworkTitleForDisplay(artworkTitle) || '-';
  const localizedMediumValue = getLocalizedArtworkMedium(mediumValue, locale);
  const formattedSize = artworkSize ? `${artworkSize.length} x ${artworkSize.height} ${artworkSize.unit}` : '-';
  const mediumLabel = [localizedMediumValue, formattedSize, yearValue || '-'].join(',\u00A0\u00A0');
  const mediumText = mediumLabel || '-';

  const shippingData = useMemo(() => getArtworkShippingConfig(artworkData), [artworkData]);
  const freeShippingAvailable = useMemo(() => {
    const { artwork_shipping_own: ownShipping, artwork_shipping_rest: restShipping } = shippingData;
    const validPayer = ['You', 'My gallery'];
    return validPayer.includes(ownShipping?.payer) && validPayer.includes(restShipping?.payer);
  }, [shippingData]);

  const isAuctionArtwork = Boolean(artworkData?.artwork_about?.artwork_is_auction);
  const auctionEndsAt = artworkData?.artwork_auction?.ends_at ?? null;
  const auctionCurrentBidPrice = artworkData?.artwork_auction?.current_bid_price ?? null;
  const auctionCurrency = artworkData?.artwork_auction?.currency ?? null;
  const resolvedCurrency = artworkCurrency || auctionCurrency || 'EUR';
  const isLimitedEditionHolding = useMemo(
    () => isLimitedEditionHoldingArtwork(artworkData),
    [artworkData],
  );
  const isOriginalSold = useMemo(() => {
    if (isLimitedEditionHolding) {
      return false;
    }
    return isArtworkOriginalSold(artworkData);
  }, [artworkData, isLimitedEditionHolding]);
  const saleSummary = useMemo(() => getArtworkSaleSummary(artworkData), [artworkData]);
  const viewerType = state?.user_session?.user_type ?? state?.user_session?.__t ?? null;
  const searchParams = new URLSearchParams(location.search);
  const holdingTypeOverride =
    searchParams.get('holding_type')
    ?? location.state?.artworkViewContext?.holdingType
    ?? null;
  const hideCollectorInteractions = shouldHideCollectorInteractions(
    artworkData,
    viewerType,
    holdingTypeOverride,
  );
  const { canOpenAskAi } = useMemo(() => getArtworkAiView(artworkData), [artworkData]);

  const artworkPriceLabel = useMemo(() => {
    if (isAuctionArtwork && auctionCurrentBidPrice) {
      const formattedBid = formatPriceWithCurrency(auctionCurrentBidPrice, resolvedCurrency);
      if (formattedBid) {
        return formattedBid;
      }
    }

    const displayedPrice = isOriginalSold ? (saleSummary?.price ?? artworkPrice) : artworkPrice;
    const displayedCurrency = isOriginalSold
      ? (saleSummary?.currency ?? resolvedCurrency)
      : resolvedCurrency;
    const formattedArtworkPrice = formatPriceWithCurrency(displayedPrice, displayedCurrency);
    return formattedArtworkPrice ?? '-';
  }, [artworkPrice, auctionCurrentBidPrice, isAuctionArtwork, isOriginalSold, resolvedCurrency, saleSummary]);

  // Keep the likes counter in sync with the backend cache.
  const handleLikeUpdate = (likeStatus) => {
    if (!socialsData) {
      return;
    }

    setSocialsData({
      ...socialsData,
      likes_count: likeStatus ? socialsData.likes_count + 1 : Math.max(socialsData.likes_count - 1, 0),
    });
  };

  // Open the artlist dialog while respecting protected actions.
  const openAddToArtlistSafely = () => {
    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.ADD_TO_ARTLIST,
        metadata: { source: 'add-to-artlist-icon__outline' },
      });
    }
    privateActionHandler(() => setOpenAddToArtlist(true));
  };

  const containerClassName = `artwork-mobile-view__container${isStandaloneVisit ? ' artwork-mobile-view__container--standalone' : ''}`;

  return (
    <>
      <div className={containerClassName}>

        <div className="artwork-mobile-view__content">
          {/* Artist information row without the duplicated go-back icon */}
          <ArtworkHeaderMobile className="artwork-mobile-view__artist" showGoBack={false} />

          {/* Artwork media slider */}
          <div className="artwork-mobile-view__pictures">
            <PicturesSection />
          </div>

          {/* Title, metadata and price rows */}
          <div className="artwork-mobile-view__label">
            <div className="artwork-label__title-icon-container">
              <AutoMarquee
                className="artwork-label__title-wrapper"
                contentClassName="artwork-label__title-scroller"
                dependencies={[titleText]}
              >
                <ArtworkTitle artworkData={artworkData} />
              </AutoMarquee>
              <div className="artwork-label__title-actions">
                {isLimitedEditionHolding ? (
                  <LimitedEditionIcon className="artwork-label__limited-edition-icon" />
                ) : null}
                {isAuctionArtwork && auctionEndsAt ? (
                  <AuctionTimeLeftIndicator
                    endsAt={auctionEndsAt}
                    className="artwork-label__auction-indicator"
                  />
                ) : null}
                {!hideCollectorInteractions && (
                  <button
                    type="button"
                    className="artwork-label__add-to-artlist-button"
                    aria-label="Add to artlist"
                    onClick={(event) => {
                      event.stopPropagation();
                      openAddToArtlistSafely();
                    }}
                  >
                    <AddToArtlistIcon className="artwork-label__add-to-artlist-icon" />
                  </button>
                )}
              </div>
            </div>

            <AutoMarquee
              className="artwork-label__medium-wrapper"
              contentClassName="artwork-label__medium-scroller"
              dependencies={[mediumText]}
            >
              <span className="artwork-label__artwork-medium">
                <Text as="span">{mediumText}</Text>
              </span>
            </AutoMarquee>

            <div className="artwork-mobile-view__label-meta">
              <div className="artwork-label__price-line">
                {isOriginalSold ? (
                  <SoldPriceValue
                    artworkData={artworkData}
                    value={artworkPriceLabel}
                    onUnlock={onUnlockSoldPrice}
                    pending={soldPricePending}
                    className="artwork-label__artwork-price"
                  />
                ) : (
                  <Text className="artwork-label__artwork-price">{artworkPriceLabel}</Text>
                )}
                {isOriginalSold && (
                  <Text className="artwork-label__sold-indicator">
                    · Sold
                  </Text>
                )}
                {freeShippingAvailable && !isAuctionArtwork && !isOriginalSold && (
                  <AutoMarquee
                    className="artwork-label__free-shipping-marquee"
                    contentClassName="artwork-label__free-shipping-track"
                    dependencies={[tPurchaseFooter('free_shipping')]}
                  >
                    <Text className="artwork-label__free-shipping">
                      {tPurchaseFooter('free_shipping')}
                    </Text>
                  </AutoMarquee>
                )}
              </div>

              {!hideCollectorInteractions && (
                <div className="artwork-mobile-view__label-socials">
                  <ArtworkSocials
                    artworkData={artworkData}
                    socialsData={socialsData}
                    onClickThought={() => setSelectedSocial('thoughts')}
                    onClickRating={() => setSelectedSocial('ratings')}
                    onClickAskAi={canOpenAskAi ? () => setSelectedSocial('ask-ai') : null}
                    onClickLike={handleLikeUpdate}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Keep mobile page lightweight: audio, description and buy section
              are accessible from the More Details bottom sheet. */}
        </div>
      </div>

      {/* Shared bottom-sheet for ratings and thoughts */}
      <Socials selectedSocial={selectedSocial} closeSocial={() => setSelectedSocial(null)} />

      {/* Add to Artlist dialog remains local to this view */}
      <AddToArtlist open={openAddToArtlist} close={() => setOpenAddToArtlist(false)} />
    </>
  );
}
